import { forwardRef, memo, useRef } from 'react';
import { Group, Rect, Text } from 'react-konva';
import Konva from 'konva';
import type { Node } from '../../../core/models/scene';
import type { NodeScenarioStatus } from '../models/Scenario';
import { buildAccentGradientStops, resolveOrgAccentColor } from './orgChartShapeColors';
import {
  shouldShowScenarioStyling,
  getKonvaDash,
  getScenarioStrokeColor,
  getScenarioStrokeWidth,
  getScenarioFillColor,
  getScenarioOpacity,
  SCENARIO_STATUS_STYLES,
} from './scenarioStatusStyles';
import { resolveOrgTextStyle } from './orgChartTextStyles';
import { useTranslation } from '../../../hooks/useTranslation';
import { resolveOrgCardDimension, useOrgCardLiveResize } from './orgCardLiveResize';

const createRoundedTopClip = (width: number, height: number, cornerRadius: number) => {
  return (ctx: any) => {
    ctx.beginPath();
    ctx.moveTo(cornerRadius, 0);
    ctx.lineTo(width - cornerRadius, 0);
    ctx.quadraticCurveTo(width, 0, width, cornerRadius);
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.lineTo(0, cornerRadius);
    ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
    ctx.closePath();
  };
};

interface DepartmentNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onTransformStart?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransformEnd?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransform?: (e: Konva.KonvaEventObject<Event>) => void;
  onStartTextEdit?: (textNode: Konva.Text) => void;
  editingPrimaryLabel?: boolean;
  canWrite?: boolean;
  scenarioStatus?: NodeScenarioStatus;
}

const DepartmentNodeInner = forwardRef<Konva.Group, DepartmentNodeProps>(({
  node,
  selected,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  onStartTextEdit,
  editingPrimaryLabel,
  canWrite = true,
  scenarioStatus,
}, ref) => {
  const { t } = useTranslation('diagram');
  const groupRef = useRef<Konva.Group | null>(null);
  const nameTextRef = useRef<Konva.Text | null>(null);

  const persistedWidth = resolveOrgCardDimension(node.w ?? node.width, 270);
  const persistedHeight = resolveOrgCardDimension(node.h ?? node.height, 60);
  const { liveSize, handleTransform, handleTransformEnd } = useOrgCardLiveResize({
    groupRef,
    width: persistedWidth,
    height: persistedHeight,
    onTransform,
    onTransformEnd,
  });
  const width = liveSize?.w ?? persistedWidth;
  const height = liveSize?.h ?? persistedHeight;

  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const props = (node.props as Record<string, unknown>) ?? {};

  const fillColor = (node.fillColor as string) || (attrs.fillColor as string) || '#FFFFFF';
  const textColor = (attrs.textColor as string) || (props.textColor as string) || '#111827';
  const accentColor = resolveOrgAccentColor(attrs, props, node.strokeColor as string);
  const accentGradientStops = buildAccentGradientStops(accentColor, 'bar');

  const defaultDepartmentName = t('orgchart.defaultDepartmentName', { defaultValue: 'New Department' }) as string;
  const name = (attrs.name as string) || (props.name as string) || (node.name as string) || defaultDepartmentName;
  const cornerRadius = 12; // rounded-xl
  const paddingX = 16;
  const paddingTop = 20;
  const nameTextStyle = resolveOrgTextStyle(attrs, { fontStyle: 'bold' }, (node as { fontFamily?: string }).fontFamily);
  const fontFamily = nameTextStyle.fontFamily ?? 'Inter, system-ui, -apple-system, sans-serif';

  const showScenarioStyle = shouldShowScenarioStyling(scenarioStatus);
  const scenarioDash = getKonvaDash(scenarioStatus);
  const scenarioOpacity = getScenarioOpacity(scenarioStatus);
  const scenarioFill = showScenarioStyle ? getScenarioFillColor(scenarioStatus, fillColor) : fillColor;

  let strokeColor = selected ? '#3B82F6' : 'transparent';
  let strokeWidth = selected ? 2 : 0;

  if (showScenarioStyle) {
    strokeColor = getScenarioStrokeColor(scenarioStatus, selected);
    strokeWidth = getScenarioStrokeWidth(scenarioStatus, selected);
  }

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const handleDoubleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!canWrite) return;
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
    if (onStartTextEdit && nameTextRef.current) {
      onStartTextEdit(nameTextRef.current);
    }
  };

  return (
    <Group
      ref={(nodeEl) => {
        groupRef.current = nodeEl;
        if (typeof ref === 'function') ref(nodeEl);
        else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<Konva.Group | null>).current = nodeEl;
      }}
      id={node.id}
      name="shape"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      draggable={canWrite && !editingPrimaryLabel}
      onDragStart={fireDrag(onDragStart)}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={fireDrag(onDragEnd)}
      onClick={onClick}
      onTransformStart={onTransformStart}
      onTransformEnd={handleTransformEnd}
      onTransform={handleTransform}
      onDblClick={handleDoubleClick}
    >
      {/* Card background */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={scenarioFill}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        cornerRadius={cornerRadius}
        shadowEnabled={false}
        dash={scenarioDash}
        dashEnabled={scenarioDash.length > 0}
        opacity={scenarioOpacity}
      />

      {/* Top gradient accent bar with rounded top corners using clipping mask */}
      <Group
        clipFunc={createRoundedTopClip(width, 4, cornerRadius)}
        listening={false}
      >
        <Rect
          x={0}
          y={0}
          width={width}
          height={4}
          fillLinearGradientStartPoint={{ x: 0, y: 0 }}
          fillLinearGradientEndPoint={{ x: width, y: 0 }}
          fillLinearGradientColorStops={accentGradientStops}
          listening={false}
        />
      </Group>

      {/* Name text */}
      <Text
        ref={nameTextRef}
        x={paddingX}
        y={paddingTop}
        width={width - paddingX * 2}
        height={Math.max(17, height - paddingTop - 8)}
        text={name}
        fontSize={13}
        fontFamily={fontFamily}
        lineHeight={17 / 13}
        wrap="word"
        fontStyle={nameTextStyle.fontStyle}
        textDecoration={nameTextStyle.textDecoration}
        fill={textColor}
        visible={!editingPrimaryLabel}
        listening={false}
        ellipsis
      />

      {/* Scenario status badge */}
      {showScenarioStyle && scenarioStatus && scenarioStatus !== 'unchanged' && (
        <Group x={width - 8} y={-8} listening={false}>
          <Rect
            x={-20}
            y={0}
            width={28}
            height={16}
            fill={SCENARIO_STATUS_STYLES[scenarioStatus].borderColor}
            cornerRadius={4}
          />
          <Text
            x={-20}
            y={0}
            width={28}
            height={16}
            text={scenarioStatus === 'added' ? '+' : scenarioStatus === 'removed' ? '−' : scenarioStatus === 'moved' ? '↔' : '∆'}
            fontSize={11}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
          />
        </Group>
      )}
    </Group>
  );
});

DepartmentNodeInner.displayName = 'DepartmentNode';

export const DepartmentNode = memo(DepartmentNodeInner);
export default DepartmentNode;
